const RESOURCE_ATTRIBUTES: Record<string, readonly string[]> = {
  link: ['href'],
  script: ['src', 'href', 'xlink:href'],
  track: ['src'],
  input: ['src'],
  frame: ['src'],
  body: ['background'],
  table: ['background'],
  thead: ['background'],
  tbody: ['background'],
  tfoot: ['background'],
  tr: ['background'],
  td: ['background'],
  th: ['background'],
  img: ['src'],
  source: ['src'],
  video: ['src', 'poster'],
  audio: ['src'],
  iframe: ['src'],
  embed: ['src'],
  object: ['data'],
  image: ['href', 'xlink:href'],
  use: ['href', 'xlink:href'],
  feImage: ['href', 'xlink:href'],
}
const FETCHING_LINK_RELS = new Set(['stylesheet', 'preload', 'modulepreload', 'prefetch'])
const CSS_ESCAPE_PLACEHOLDERS = '() \t\n\r\f@;{}:[]'
const FRAGMENT_REFERENCE_PROPERTIES = new Set([
  'fill',
  'stroke',
  'filter',
  'clip-path',
  'mask',
  'mask-image',
  'marker',
  'marker-start',
  'marker-mid',
  'marker-end',
])
const SVG_RESOURCE_ATTRIBUTES = [
  'fill',
  'stroke',
  'clip-path',
  'mask',
  'filter',
  'marker-start',
  'marker-mid',
  'marker-end',
  'cursor',
]
const SVG_ANIMATION_ELEMENTS = new Set(['set', 'animate', 'animateColor', 'animateMotion', 'animateTransform'])
const SVG_ANIMATION_VALUE_ATTRIBUTES = ['to', 'from', 'by', 'values']

function isSupportedUrl(value: string, allowFragment = false): boolean {
  let start = 0
  let end = value.length
  while (start < end && value.charCodeAt(start) <= 0x20) start++
  while (end > start && value.charCodeAt(end - 1) <= 0x20) end--
  const reference = value.slice(start, end)
  if (allowFragment && value.startsWith('#') && reference.length > 1) return true
  try {
    if (!['https:', 'data:'].includes(new URL(reference, 'about:blank').protocol)) return false
    const first = new URL(reference, 'https://base-one.invalid/dir/')
    const second = new URL(reference, 'https://base-two.invalid/other/')
    return first.href === second.href && ['https:', 'data:'].includes(first.protocol)
  } catch {
    return false
  }
}

function isSupportedStylesheet(value: string, dataDepth = 0): boolean {
  if (!isSupportedUrl(value)) return false
  const url = new URL(value, 'about:blank')
  if (url.protocol !== 'data:') return true
  url.hash = ''
  const comma = url.href.indexOf(',')
  if (comma === -1 || dataDepth >= 4) return false
  const metadata = url.href.slice(5, comma)
  let hasUtf8Charset = false
  let foundCharset = false
  for (const parameter of metadata.split(';').slice(1)) {
    const charset = parameter.match(/^[\t\n\f\r ]*charset[\t\n\f\r ]*=(.*)$/i)?.[1]
    if (
      charset !== undefined &&
      !/^[\t\n\f\r ]*(?:"(?:utf-8|utf8|us-ascii)"|'(?:utf-8|utf8|us-ascii)'|utf-8|utf8|us-ascii)[\t\n\f\r ]*$/i.test(
        charset,
      )
    )
      return false
    const trimmed = parameter.replace(/^[\t\n\f\r ]+/, '')
    if (!foundCharset && /^charset=/i.test(trimmed)) {
      foundCharset = true
      hasUtf8Charset = /^charset=(?:utf-?8|"utf-?8")$/i.test(trimmed)
    }
  }
  let decoded: string
  let hasUtf8Bom = false
  try {
    const bytes = new TextEncoder().encode(url.href.slice(comma + 1))
    let length = 0
    for (let position = 0; position < bytes.length; position++) {
      const byte = bytes[position] ?? 0
      const hex = byte === 0x25 ? String.fromCharCode(...bytes.subarray(position + 1, position + 3)) : ''
      if (/^[\da-f]{2}$/i.test(hex)) {
        bytes[length++] = parseInt(hex, 16)
        position += 2
      } else {
        bytes[length++] = byte
      }
    }
    let payload = bytes.subarray(0, length)
    if (/;[\t\n\f\r ]*base64[\t\n\f\r ]*$/i.test(metadata)) {
      const binary = atob(new TextDecoder('utf-8', {fatal: true, ignoreBOM: true}).decode(payload))
      payload = Uint8Array.from(binary, character => character.charCodeAt(0))
    }
    hasUtf8Bom = payload[0] === 0xef && payload[1] === 0xbb && payload[2] === 0xbf
    decoded = new TextDecoder('utf-8', {fatal: true}).decode(payload)
  } catch {
    return false
  }
  const charset = decoded.match(/^@charset "([^"]*)"/i)?.[1]
  if (
    (!hasUtf8Bom && charset !== undefined && charset.toLowerCase() !== 'utf-8') ||
    decoded.includes('\u0000') ||
    decoded.includes('\u001b')
  )
    return false
  if (!hasUtf8Bom && !hasUtf8Charset && !/^@charset "[Uu][Tt][Ff]-8";/.test(decoded)) {
    for (const character of decoded) {
      if (character.charCodeAt(0) >= 0x80) return false
    }
  }
  return isSupportedCss(decoded, dataDepth + 1)
}

function isSupportedRefresh(value: string): boolean {
  let position = 0
  while (position < value.length && /[\t\n\f\r ]/.test(value.charAt(position))) position++
  const timeStart = position
  while (position < value.length && /[\d.]/.test(value.charAt(position))) position++
  if (position === timeStart) return false
  while (position < value.length && /[\t\n\f\r ]/.test(value.charAt(position))) position++
  if (value.charAt(position) === ';' || value.charAt(position) === ',') position++
  while (position < value.length && /[\t\n\f\r ]/.test(value.charAt(position))) position++
  const destination = value.slice(position).replace(/^url[\t\n\f\r ]*=[\t\n\f\r ]*/i, '')
  if (destination === '') return true
  return isSupportedUrl(destination.replace(/^(['"])([\s\S]*)\1[\t\n\f\r ]*$/, '$2'))
}

export function isSupportedCss(value: string, dataDepth = 0, property?: string): boolean {
  if (/[\uFDD0-\uFDEF]/.test(value)) return false
  const tokens =
    /"(?:[^"\\\r\n\f]|\\[\s\S])*(?:"|(?=[\r\n\f])|$)|'(?:[^'\\\r\n\f]|\\[\s\S])*(?:'|(?=[\r\n\f])|$)|\/\*[\s\S]*?(?:\*\/|$)|\\[0-9a-fA-F]{1,6}(?:\r\n|[\t\n\f\r ])?|\\[\s\S]|[()]|\s+|[;{}]|[^"'\\/\s();{}]+|[\s\S]/g
  const content: string[] = []
  let parentheses = 0
  let isUnquotedArgument = false
  let hasArgumentToken = false
  let functionName = ''
  let hasFunctionEscape = false
  let isUrlArgument = false
  let hasUrlToken = false
  let hasUrlWhitespace = false
  for (const [token] of value.matchAll(tokens)) {
    if (token.startsWith('"') || token.startsWith("'")) {
      if (token.length < 2 || token.at(-1) !== token[0] || !/(?:^|[^\\])(?:\\\\)*["']$/.test(token)) {
        return false
      }
      if (isUrlArgument && hasUrlToken) return false
      isUrlArgument = false
      functionName = ''
      hasFunctionEscape = false
      isUnquotedArgument = false
    } else if (token.startsWith('/*')) {
      if (token.length < 4 || !token.endsWith('*/') || isUnquotedArgument || isUrlArgument) return false
      content.push(' ')
      continue
    } else if (token.startsWith('\\')) {
      if (isUrlArgument) {
        if (hasUrlWhitespace) return false
        hasUrlToken = true
      }
      hasFunctionEscape = true
      if (isUnquotedArgument) hasArgumentToken = true
    } else {
      for (const character of token) {
        const isWhitespace = /[\t\n\f\r ]/.test(character)
        if (isUrlArgument && character !== ')') {
          if (isWhitespace) {
            if (hasUrlToken) hasUrlWhitespace = true
          } else {
            if (hasUrlWhitespace || character === '(') return false
            hasUrlToken = true
          }
        }
        if (character === '(') {
          parentheses++
          isUnquotedArgument = hasFunctionEscape
          isUrlArgument = functionName.toLowerCase() === 'url' || hasFunctionEscape
          hasUrlToken = false
          hasUrlWhitespace = false
          functionName = ''
          hasFunctionEscape = false
          hasArgumentToken = false
        } else if (/[)"';{}]/.test(character)) {
          if (character === ')') {
            if (parentheses === 0) return false
            parentheses--
            isUrlArgument = false
          }
          functionName = ''
          hasFunctionEscape = false
          isUnquotedArgument = false
        } else if (isWhitespace) {
          functionName = ''
          hasFunctionEscape = false
          if (hasArgumentToken) isUnquotedArgument = false
        } else {
          if (/[a-z0-9_-]/i.test(character) || character.charCodeAt(0) >= 0x80) {
            functionName = (functionName + character).slice(-3)
          } else {
            functionName = ''
            hasFunctionEscape = false
          }
          if (isUnquotedArgument) hasArgumentToken = true
        }
      }
    }
    content.push(token)
  }
  if (parentheses !== 0) return false
  const withoutComments = content.join('')
  if (/\\(?:[\r\n\f]|$)/.test(withoutComments)) return false
  let validEscapes = true
  const css = withoutComments.replace(
    /\\([0-9a-f]{1,6})(?:\r\n|[\t\n\f\r ])?|\\(.)/gi,
    (_match, hex: string | undefined, character: string | undefined) => {
      if (hex === undefined) {
        if (character === '"' || character === "'") validEscapes = false
        const index = character === undefined ? -1 : CSS_ESCAPE_PLACEHOLDERS.indexOf(character)
        if (index !== -1) return String.fromCharCode(0xfdd0 + index)
        return character ?? ''
      }
      const codePoint = parseInt(hex, 16)
      if (
        codePoint === 0 ||
        codePoint > 0x10ffff ||
        (codePoint >= 0xd800 && codePoint <= 0xdfff) ||
        (codePoint >= 0xfdd0 && codePoint <= 0xfdef)
      ) {
        validEscapes = false
        return ''
      }
      const decoded = String.fromCodePoint(codePoint)
      if (decoded === '"' || decoded === "'") validEscapes = false
      const index = CSS_ESCAPE_PLACEHOLDERS.indexOf(decoded)
      if (index !== -1) return String.fromCharCode(0xfdd0 + index)
      return decoded
    },
  )
  if (!validEscapes) return false
  const resources =
    /@namespace[\t\n\f\r ]+(?:[-_a-z][-_a-z0-9]*[\t\n\f\r ]+)?(?:url[\t\n\f\r ]*\([\t\n\f\r ]*(?:"[^"]*"|'[^']*'|[^"'()\t\n\f\r ]+)[\t\n\f\r ]*\)|"[^"]*"|'[^']*')[\t\n\f\r ]*;|(?:@import[\t\n\f\r ]+)?url[\t\n\f\r ]*\([\t\n\f\r ]*(?:"([^"]*)"|'([^']*)'|([^"'()\t\n\f\r ]+))[\t\n\f\r ]*\)|@import[\t\n\f\r ]*(?:"([^"]*)"|'([^']*)')|"[^"]*"|'[^']*'/gi
  let previousEnd = 0
  let declaration = ''
  let sawBoundary = false
  const stack: string[] = []
  for (const match of css.matchAll(resources)) {
    const gap = css.slice(previousEnd, match.index)
    for (const character of gap) {
      if (character === '(' || character === '[' || (character === '{' && stack.length > 0)) {
        stack.push(character === '(' ? ')' : character === '[' ? ']' : '}')
        continue
      }
      if (stack.length > 0 && character === stack.at(-1)) {
        stack.pop()
        continue
      }
      if (stack.length === 0) {
        if (character === ';' || character === '{' || character === '}') {
          sawBoundary = true
          declaration = ''
        } else {
          declaration += character
        }
      }
    }
    previousEnd = match.index + match[0].length
    if (stack.length === 0 && /^@namespace/i.test(match[0])) {
      sawBoundary = true
      declaration = ''
      continue
    }
    const nested = stack.length > 0
    if (!nested) declaration += ' '
    const reference = (match[1] ?? match[2] ?? match[3] ?? match[4] ?? match[5])?.replace(
      /[\uFDD0-\uFDDD]/g,
      character => CSS_ESCAPE_PLACEHOLDERS.charAt(character.charCodeAt(0) - 0xfdd0),
    )
    if (reference === undefined) continue
    const colon = declaration.indexOf(':')
    const name =
      colon !== -1
        ? declaration.slice(0, colon).trim().toLowerCase()
        : sawBoundary
          ? ''
          : (property ?? '').toLowerCase()
    if (
      /^@import/i.test(match[0])
        ? !isSupportedStylesheet(reference, dataDepth)
        : !isSupportedUrl(reference, !nested && FRAGMENT_REFERENCE_PROPERTIES.has(name))
    )
      return false
  }
  return !/url\s*\(|@import|image-set\s*\(|["']/i.test(css.replace(resources, ''))
}

function isSupportedSrcset(value: string): boolean {
  let position = 0
  let candidates = 0
  while (position < value.length) {
    while (position < value.length && /[\t\n\f\r ,]/.test(value.charAt(position))) position++
    if (position === value.length) break
    const start = position
    while (position < value.length && !/[\t\n\f\r ]/.test(value.charAt(position))) position++
    let urlEnd = position
    while (urlEnd > start && value.charAt(urlEnd - 1) === ',') urlEnd--
    const reference = value.slice(start, urlEnd)
    if (!isSupportedUrl(reference)) return false
    candidates++
    if (urlEnd !== position) continue

    let descriptorStart = position
    let parentheses = 0
    while (position < value.length) {
      const character = value.charAt(position)
      if (character === ',' && parentheses === 0) break
      if (character === '(') parentheses++
      if (character === ')') {
        if (parentheses === 0) return false
        parentheses--
      }
      position++
    }
    let descriptorEnd = position
    while (descriptorStart < descriptorEnd && /[\t\n\f\r ]/.test(value.charAt(descriptorStart))) descriptorStart++
    while (descriptorEnd > descriptorStart && /[\t\n\f\r ]/.test(value.charAt(descriptorEnd - 1))) descriptorEnd--
    const descriptor = value.slice(descriptorStart, descriptorEnd)
    if (parentheses !== 0 || (descriptor !== '' && !/^(?:[1-9]\d*w|(?:\d+(?:\.\d+)?|\.\d+)x)$/.test(descriptor))) {
      return false
    }
    if (position < value.length) position++
  }
  return candidates > 0
}

export function isArtifactHtmlPreviewEligible(html: string): boolean | undefined {
  if (typeof DOMParser === 'undefined') return undefined
  const rewritten = html.replace(/<(\/?)noscript(?=[\t\n\f\r />]|$)/gi, '<$1noframes')
  let parsed: Document
  try {
    parsed = new DOMParser().parseFromString(rewritten, 'text/html')
  } catch {
    return undefined
  }

  if (rewritten !== html && (/noframes/i.test(html) || parsed.querySelector('frameset') !== null)) return false

  for (const element of parsed.querySelectorAll('*')) {
    const tag = element.localName
    if (tag === 'base' && element.hasAttribute('href')) return false
    if (tag === 'iframe' && element.hasAttribute('srcdoc')) return false
    if (tag === 'meta' && element.getAttribute('http-equiv')?.trim().toLowerCase() === 'refresh') {
      const refresh = element.getAttribute('content')
      if (refresh !== null && !isSupportedRefresh(refresh)) return false
    }
    if (tag === 'template' && (element.hasAttribute('shadowrootmode') || element.hasAttribute('shadowroot'))) {
      return false
    }
    if (
      tag === 'script' &&
      ['module', 'importmap'].includes(element.getAttribute('type')?.trim().toLowerCase() ?? '')
    ) {
      return false
    }
    const linkRels = tag === 'link' ? (element.getAttribute('rel') ?? '').toLowerCase().split(/\s+/) : []
    const isFetchingLink = tag !== 'link' || linkRels.some(rel => FETCHING_LINK_RELS.has(rel))
    const isStylesheet =
      tag === 'link' && element.namespaceURI === 'http://www.w3.org/1999/xhtml' && linkRels.includes('stylesheet')
    const isFetchingInput = tag !== 'input' || element.getAttribute('type')?.trim().toLowerCase() === 'image'
    if (isFetchingLink && isFetchingInput && Object.hasOwn(RESOURCE_ATTRIBUTES, tag)) {
      for (const attribute of RESOURCE_ATTRIBUTES[tag] ?? []) {
        const value = element.getAttribute(attribute)
        if (value === null) continue
        if (isStylesheet ? !isSupportedStylesheet(value) : !isSupportedUrl(value, tag === 'use' || tag === 'feImage'))
          return false
        if (['iframe', 'frame', 'object', 'embed'].includes(tag) && new URL(value, 'about:blank').protocol === 'data:')
          return false
      }
    }
    if (tag === 'link' && isFetchingLink) {
      const imagesrcset = element.getAttribute('imagesrcset')
      if (imagesrcset !== null && !isSupportedSrcset(imagesrcset)) return false
    }
    if (tag === 'img' || tag === 'source') {
      const srcset = element.getAttribute('srcset')
      if (srcset !== null && !isSupportedSrcset(srcset)) return false
    }
    const style = element.getAttribute('style')
    if (style !== null && !isSupportedCss(style)) return false
    if (element.namespaceURI === 'http://www.w3.org/2000/svg') {
      for (const attribute of SVG_RESOURCE_ATTRIBUTES) {
        const value = element.getAttribute(attribute)
        if (value !== null && !isSupportedCss(value, 0, attribute)) return false
      }
      if (SVG_ANIMATION_ELEMENTS.has(tag)) {
        const target = (element.getAttribute('attributeName') ?? '')
          .trim()
          .toLowerCase()
          .replace(/^[^:]*:/, '')
        const isUrlTarget = target === 'href' || target === 'src'
        const isCssTarget = target === 'style' || SVG_RESOURCE_ATTRIBUTES.includes(target)
        for (const attribute of SVG_ANIMATION_VALUE_ATTRIBUTES) {
          const value = element.getAttribute(attribute)
          if (value === null) continue
          const items = attribute === 'values' ? value.split(';') : [value]
          if (isUrlTarget || (isCssTarget && items.some(item => !isSupportedCss(item.trim(), 0, target)))) return false
        }
      }
    }
    if (tag === 'style' && !isSupportedCss(element.textContent ?? '')) return false
  }
  return true
}
